import React, { useEffect } from "react";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import type { User } from "firebase/auth";
import type { PlanId, PlanLimits } from "../../../api/organizations";
import type { OrganizationRecord } from "../../../api/organizations";
import type { UpdateBillingParams } from "../../../api/organizations";
import { PLAN_LIMITS, createStripePriceForCustomPlan } from "../../../api/organizations";
import {
  PLAN_IDS,
  PLAN_IDS_WITH_CUSTOM,
  SUBSCRIPTION_STATUSES,
} from "../constants";

type OrgPayMethod = "stripe" | "ves";

const toggleOrgPayMethod = (
  prev: OrgPayMethod[],
  method: OrgPayMethod,
  on: boolean,
): OrgPayMethod[] => {
  if (on) {
    return prev.includes(method) ? prev : [...prev, method];
  }
  return prev.filter((m) => m !== method);
};

interface SetPlanAndStatusModalProps {
  show: boolean;
  onHide: () => void;
  org: OrganizationRecord | null;
  planCatalog: Record<PlanId, PlanLimits>;
  saving: boolean;
  user: User;
  onSave: (orgId: string, updates: UpdateBillingParams) => Promise<void>;
}

export const SetPlanAndStatusModal: React.FC<SetPlanAndStatusModalProps> = ({
  show,
  onHide,
  org,
  planCatalog,
  saving,
  user,
  onSave,
}) => {
  const [editStatus, setEditStatus] = React.useState<string>("active");
  const [editPlanId, setEditPlanId] = React.useState<PlanId>("standard");
  const [editPeriodEnd, setEditPeriodEnd] = React.useState("");
  const [editPlanName, setEditPlanName] = React.useState("Standard");
  const [editBillingType, setEditBillingType] = React.useState<"fixed" | "quote">("fixed");
  const [editCurrency, setEditCurrency] = React.useState("USD");
  const [editMonthlyPrice, setEditMonthlyPrice] = React.useState("29.99");
  const [editStripeCustomerId, setEditStripeCustomerId] = React.useState("");
  const [editStripeSubscriptionId, setEditStripeSubscriptionId] = React.useState("");
  const [editStripePriceId, setEditStripePriceId] = React.useState("");
  const [editPaymentMethodsAllowed, setEditPaymentMethodsAllowed] = React.useState<
    OrgPayMethod[]
  >(["stripe", "ves"]);
  const [editSelectablePlanIds, setEditSelectablePlanIds] = React.useState<string[]>([]);
  const [editMaxSalesPeople, setEditMaxSalesPeople] = React.useState("20");
  const [editMaxCustomers, setEditMaxCustomers] = React.useState("1000");
  const [editMaxInventoryItems, setEditMaxInventoryItems] = React.useState("2500");
  const [editMaxSuppliers, setEditMaxSuppliers] = React.useState("300");
  const [editMaxAdmins, setEditMaxAdmins] = React.useState("5");

  const applyPlanSelection = (planId: PlanId) => {
    const selected = planCatalog[planId] ?? PLAN_LIMITS[planId];
    if (!selected) return;
    setEditPlanName(selected.name);
    setEditBillingType((selected.billingType as "fixed" | "quote") || "fixed");
    setEditCurrency(selected.currency || "USD");
    setEditMonthlyPrice(
      selected.monthlyPrice === null || selected.monthlyPrice === undefined
        ? ""
        : String(selected.monthlyPrice),
    );
    setEditMaxSalesPeople(String(selected.maxSalesPeople));
    setEditMaxCustomers(String(selected.maxCustomers));
    setEditMaxInventoryItems(String(selected.maxInventoryItems));
    setEditMaxSuppliers(String(selected.maxSuppliers));
    setEditMaxAdmins(String(selected.maxAdmins));
    setEditStripePriceId(selected.stripePriceId || "");
  };

  useEffect(() => {
    if (!show || !org) return;
    setEditStatus(org.subscriptionStatus ?? "active");
    const effectivePlanId = (org.planId ?? "standard") as PlanId;
    setEditPlanId(effectivePlanId);
    setEditPeriodEnd(
      org.currentPeriodEnd ? org.currentPeriodEnd.toISOString().slice(0, 10) : "",
    );
    applyPlanSelection(effectivePlanId);
    const p = org.plan;
    if (p) {
      if (p.name) setEditPlanName(p.name);
      if (p.billingType === "fixed" || p.billingType === "quote") setEditBillingType(p.billingType);
      if (typeof p.currency === "string" && p.currency) setEditCurrency(p.currency);
      if (typeof p.monthlyPrice === "number") setEditMonthlyPrice(String(p.monthlyPrice));
      if (typeof p.maxSalesPeople === "number") setEditMaxSalesPeople(String(p.maxSalesPeople));
      if (typeof p.maxCustomers === "number") setEditMaxCustomers(String(p.maxCustomers));
      if (typeof p.maxInventoryItems === "number")
        setEditMaxInventoryItems(String(p.maxInventoryItems));
      if (typeof p.maxSuppliers === "number")
        setEditMaxSuppliers(String(p.maxSuppliers));
      if (typeof p.maxAdmins === "number") setEditMaxAdmins(String(p.maxAdmins));
    }
    setEditStripeCustomerId(org.stripeCustomerId ?? "");
    setEditStripeSubscriptionId(org.stripeSubscriptionId ?? "");
    setEditStripePriceId(org.stripePriceId ?? org.plan?.stripePriceId ?? "");
    setEditPaymentMethodsAllowed(
      org.paymentMethodsAllowed && org.paymentMethodsAllowed.length > 0
        ? org.paymentMethodsAllowed.filter(
            (m): m is OrgPayMethod => m === "stripe" || m === "ves",
          )
        : ["stripe", "ves"],
    );
    setEditSelectablePlanIds(org.selectablePlanIds ?? []);
  }, [show, org?.id]);

  const handleSave = async () => {
    if (!org) return;
    if (editPaymentMethodsAllowed.length === 0) {
      return;
    }
    const toNonNegativeInt = (value: string) =>
      Math.max(0, parseInt(value || "0", 10) || 0);
    const parsedPrice =
      editBillingType === "quote"
        ? null
        : Number((editMonthlyPrice || "0").replace(",", "."));
    if (
      editBillingType === "fixed" &&
      (!Number.isFinite(parsedPrice as number) || (parsedPrice as number) < 0)
    ) {
      throw new Error("Monthly price must be a valid non-negative number.");
    }
    const catalogPlan = planCatalog[editPlanId] ?? PLAN_LIMITS[editPlanId];
    const isCustomPlan = editPlanId === "custom";
    
    // Auto-create Stripe price if custom plan has monthly price but no stripePriceId
    let finalStripePriceId = editStripePriceId.trim();
    let finalStripeProductId: string | undefined = undefined;
    
    if (isCustomPlan && editBillingType === "fixed" && parsedPrice !== null && !finalStripePriceId) {
      try {
        const stripeData = await createStripePriceForCustomPlan(
          user,
          org.id,
          editPlanName || "Custom",
          parsedPrice,
          editCurrency || "USD"
        );
        finalStripePriceId = stripeData.stripePriceId;
        finalStripeProductId = stripeData.stripeProductId;
      } catch (error) {
        throw new Error(
          `Failed to create Stripe price: ${error instanceof Error ? error.message : "Unknown error"}`
        );
      }
    }
    
    const customPlan = isCustomPlan
      ? {
          id: "custom",
          name: editPlanName || "Custom",
          billingType: editBillingType,
          currency: editCurrency || "USD",
          monthlyPrice: parsedPrice,
          maxSalesPeople: toNonNegativeInt(editMaxSalesPeople),
          maxCustomers: toNonNegativeInt(editMaxCustomers),
          maxInventoryItems: toNonNegativeInt(editMaxInventoryItems),
          maxSuppliers: toNonNegativeInt(editMaxSuppliers),
          maxAdmins: toNonNegativeInt(editMaxAdmins),
          stripePriceId: finalStripePriceId || undefined,
          stripeProductId: finalStripeProductId || undefined,
        }
      : {
          id: catalogPlan.id,
          name: catalogPlan.name,
          billingType: catalogPlan.billingType ?? "fixed",
          currency: catalogPlan.currency ?? "USD",
          monthlyPrice: catalogPlan.monthlyPrice ?? parsedPrice,
          maxSalesPeople: catalogPlan.maxSalesPeople,
          maxCustomers: catalogPlan.maxCustomers,
          maxInventoryItems: catalogPlan.maxInventoryItems,
          maxSuppliers: catalogPlan.maxSuppliers,
          maxAdmins: catalogPlan.maxAdmins,
        };
    const updates: UpdateBillingParams = {
      subscriptionStatus: editStatus as UpdateBillingParams["subscriptionStatus"],
      planId: isCustomPlan ? "custom" : editPlanId,
      customPlan,
      stripeCustomerId: editStripeCustomerId.trim() || null,
      stripeSubscriptionId: editStripeSubscriptionId.trim() || null,
      stripePriceId: finalStripePriceId || null,
      paymentMethodsAllowed: editPaymentMethodsAllowed,
      selectablePlanIds:
        editSelectablePlanIds.length > 0 ? editSelectablePlanIds : null,
    };
    if (editPeriodEnd) {
      updates.currentPeriodEnd = new Date(editPeriodEnd);
    }
    await onSave(org.id, updates);
    onHide();
  };

  return (
    <Modal show={show} onHide={() => !saving && onHide()} centered>
      <Modal.Header closeButton>
        <Modal.Title>Set plan &amp; status</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {org && (
          <>
            <p className="text-muted small mb-3">
              {org.name} ({org.id})
            </p>
            <Form.Group className="mb-3">
              <Form.Label>Subscription status</Form.Label>
              <Form.Select
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
              >
                {SUBSCRIPTION_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Plan</Form.Label>
              <Form.Select
                value={editPlanId}
                onChange={(e) => {
                  const selectedPlanId = e.target.value as PlanId;
                  setEditPlanId(selectedPlanId);
                  applyPlanSelection(selectedPlanId);
                }}
              >
                {PLAN_IDS_WITH_CUSTOM.map((p) => (
                  <option key={p} value={p}>
                    {(planCatalog[p] ?? PLAN_LIMITS[p])?.name ?? p}
                  </option>
                ))}
              </Form.Select>
              <Form.Text muted>
                Only &quot;Custom&quot; can have its limits and price changed;
                catalog plans use fixed catalog values.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Plan display name</Form.Label>
              <Form.Control
                value={editPlanName}
                onChange={(e) => setEditPlanName(e.target.value)}
                placeholder="Plan name shown to organization"
                disabled={editPlanId !== "custom"}
                readOnly={editPlanId !== "custom"}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Billing type</Form.Label>
              <Form.Select
                value={editBillingType}
                onChange={(e) =>
                  setEditBillingType(e.target.value as "fixed" | "quote")
                }
                disabled={editPlanId !== "custom"}
              >
                <option value="fixed">Fixed monthly price</option>
                <option value="quote">Quote-based</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Currency</Form.Label>
              <Form.Control
                value={editCurrency}
                onChange={(e) => setEditCurrency(e.target.value.toUpperCase())}
                maxLength={6}
                placeholder="USD"
                disabled={editPlanId !== "custom"}
                readOnly={editPlanId !== "custom"}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Monthly price (reference)</Form.Label>
              <InputGroup>
                <InputGroup.Text>{editCurrency || "USD"}</InputGroup.Text>
                <Form.Control
                  value={editMonthlyPrice}
                  onChange={(e) => setEditMonthlyPrice(e.target.value)}
                  disabled={
                    editBillingType === "quote" || editPlanId !== "custom"
                  }
                  readOnly={editPlanId !== "custom"}
                  placeholder={editBillingType === "quote" ? "Quote" : "0.00"}
                />
              </InputGroup>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Stripe price ID</Form.Label>
              <Form.Control
                value={editStripePriceId}
                onChange={(e) => setEditStripePriceId(e.target.value)}
                placeholder="price_..."
                disabled={editPlanId !== "custom"}
                readOnly={editPlanId !== "custom"}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Stripe customer ID (optional)</Form.Label>
              <Form.Control
                value={editStripeCustomerId}
                onChange={(e) => setEditStripeCustomerId(e.target.value)}
                placeholder="cus_..."
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Stripe subscription ID (optional)</Form.Label>
              <Form.Control
                value={editStripeSubscriptionId}
                onChange={(e) => setEditStripeSubscriptionId(e.target.value)}
                placeholder="sub_..."
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Allowed payment methods</Form.Label>
              <div className="d-flex flex-column gap-2">
                <Form.Check
                  type="checkbox"
                  id="pm-stripe"
                  label="Card (Stripe)"
                  checked={editPaymentMethodsAllowed.includes("stripe")}
                  disabled={
                    editPaymentMethodsAllowed.length === 1 &&
                    editPaymentMethodsAllowed.includes("stripe")
                  }
                  onChange={(e) =>
                    setEditPaymentMethodsAllowed((prev) =>
                      toggleOrgPayMethod(prev, "stripe", e.target.checked),
                    )
                  }
                />
                <Form.Check
                  type="checkbox"
                  id="pm-ves"
                  label="Bolívares (VES)"
                  checked={editPaymentMethodsAllowed.includes("ves")}
                  disabled={
                    editPaymentMethodsAllowed.length === 1 &&
                    editPaymentMethodsAllowed.includes("ves")
                  }
                  onChange={(e) =>
                    setEditPaymentMethodsAllowed((prev) =>
                      toggleOrgPayMethod(prev, "ves", e.target.checked),
                    )
                  }
                />
              </div>
              <Form.Text muted>
                Further restrict this org among methods enabled in Payment
                methods. Keep at least one. Binance Pay is not offered yet.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Plans this org can choose from</Form.Label>
              <div className="d-flex flex-wrap gap-3">
                {PLAN_IDS.map((planId) => (
                  <Form.Check
                    key={planId}
                    type="checkbox"
                    id={`selectable-${planId}`}
                    label={
                      (planCatalog[planId] ?? PLAN_LIMITS[planId])?.name ?? planId
                    }
                    checked={editSelectablePlanIds.includes(planId)}
                    onChange={(e) =>
                      setEditSelectablePlanIds((prev) =>
                        e.target.checked
                          ? [...prev, planId]
                          : prev.filter((id) => id !== planId),
                      )
                    }
                  />
                ))}
              </div>
              <Form.Text muted>
                When the org can pick a plan (e.g. trialing or new), only
                these plans appear. Leave all unchecked to show all plans.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Max sales people</Form.Label>
              <Form.Control
                type="number"
                min={0}
                value={editMaxSalesPeople}
                onChange={(e) => setEditMaxSalesPeople(e.target.value)}
                disabled={editPlanId !== "custom"}
                readOnly={editPlanId !== "custom"}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Max customers</Form.Label>
              <Form.Control
                type="number"
                min={0}
                value={editMaxCustomers}
                onChange={(e) => setEditMaxCustomers(e.target.value)}
                disabled={editPlanId !== "custom"}
                readOnly={editPlanId !== "custom"}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Max inventory items</Form.Label>
              <Form.Control
                type="number"
                min={0}
                value={editMaxInventoryItems}
                onChange={(e) => setEditMaxInventoryItems(e.target.value)}
                disabled={editPlanId !== "custom"}
                readOnly={editPlanId !== "custom"}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Max suppliers</Form.Label>
              <Form.Control
                type="number"
                min={0}
                value={editMaxSuppliers}
                onChange={(e) => setEditMaxSuppliers(e.target.value)}
                disabled={editPlanId !== "custom"}
                readOnly={editPlanId !== "custom"}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Max admins</Form.Label>
              <Form.Control
                type="number"
                min={0}
                value={editMaxAdmins}
                onChange={(e) => setEditMaxAdmins(e.target.value)}
                disabled={editPlanId !== "custom"}
                readOnly={editPlanId !== "custom"}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Current period end (optional)</Form.Label>
              <Form.Control
                type="date"
                value={editPeriodEnd}
                onChange={(e) => setEditPeriodEnd(e.target.value)}
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving || editPaymentMethodsAllowed.length === 0}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
